import {
  useCallback, useEffect, useRef, useState,
} from 'react'

import { fetchCommunityModelLikers } from 'Pods/Shared/CommunityModelLike/communityModelLikeApi'

import { LIKERS_PER_PAGE } from '../constants'
import { normalizeLikersList } from '../Utils/normalizeLikers'

// Owns the paginated list of users who liked the opened model. The first page is
// fetched eagerly rather than on popover open, because the avatar stack in the
// footer renders the first rows before anything is opened.
//
// `isToggling` drives the refresh, not `likeCount`: the shared toggle hook
// reports the new count optimistically, before the write lands, so a count-keyed
// refetch would race the POST/DELETE and read the row it is about to change -
// and never re-read, since the authoritative response repeats the count it
// already reported. Reading once the toggle has settled avoids both.
export default function useModelLikers({
  slug, likeCount = 0, isToggling = false, enabled = false,
}) {
  const [likers, setLikers] = useState([])
  const [loadedPage, setLoadedPage] = useState(0)
  const [totalItemsCount, setTotalItemsCount] = useState(0)
  const [perPage, setPerPage] = useState(LIKERS_PER_PAGE)
  const [isLoading, setIsLoading] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)

  // Every read carries the token of the generation that started it, and a response
  // whose token has been superseded is dropped. Navigating to another model is one
  // such supersession; so is a same-slug re-read, which the authoritative read
  // after a settled toggle performs - it can overlap a page still in flight, and
  // the older answer must not overwrite the newer rows or counts.
  const requestIdRef = useRef(0)

  const isActive = enabled && !!slug && likeCount > 0

  // Page boundaries, not row counts: rows whose account is gone are dropped by
  // the normalizer, so comparing loaded rows against the total would keep
  // fetching an already exhausted list forever.
  const hasMore = loadedPage > 0 && loadedPage * perPage < totalItemsCount

  const fetchPage = useCallback(async (page, requestId) => {
    setIsLoading(true)

    try {
      const payload = await fetchCommunityModelLikers({ slug, page })

      if (requestIdRef.current !== requestId) return

      const rows = normalizeLikersList(payload)
      const meta = payload?.meta

      setLikers(prev => (page === 1 ? rows : [...prev, ...rows]))
      setLoadedPage(page)

      if (typeof meta?.totalItemsCount === 'number') setTotalItemsCount(meta.totalItemsCount)
      if (typeof meta?.perPage === 'number' && meta.perPage > 0) setPerPage(meta.perPage)
    } catch {
      if (requestIdRef.current !== requestId) return

      // A list that cannot be read is not worth an error surface: the stack and
      // the popover simply stay on what they already have. The flag stops
      // further pages so a failing endpoint is not polled on every scroll.
      setLoadFailed(true)
    } finally {
      // Left to the newer read when superseded: it owns the flag it just raised.
      if (requestIdRef.current === requestId) setIsLoading(false)
    }
  }, [slug])

  useEffect(() => {
    // Opens a new generation, retiring whatever is already in flight.
    requestIdRef.current += 1

    if (!isActive) {
      setLikers([])
      setLoadedPage(0)
      setTotalItemsCount(0)
      setLoadFailed(false)

      return
    }

    // Mid-toggle the rows on screen are the optimistic ones; the run below, once
    // isToggling drops, is the authoritative read.
    if (isToggling) return

    setLoadFailed(false)
    fetchPage(1, requestIdRef.current)
  }, [isActive, slug, isToggling, fetchPage])

  const loadMore = useCallback(() => {
    if (isLoading || loadFailed || !hasMore) return

    // Rides the current generation: an appended page extends the page one already
    // on screen, so it is retired only by the next read, never by its own siblings.
    fetchPage(loadedPage + 1, requestIdRef.current)
  }, [isLoading, loadFailed, hasMore, loadedPage, fetchPage])

  return {
    likers,
    // The header count follows the server total once it lands, and the seeded
    // like count until then, so it never flashes zero behind the stack.
    totalCount: totalItemsCount || likeCount,
    hasMore,
    isLoading,
    loadFailed,
    loadMore,
  }
}
